/**
 * RecentScriptsSection
 *
 * Start Page section showing the 5 most recent scripts for quick access.
 * Hidden when no scripts have been tracked. Follows the same collapsible
 * pattern as RecentTabsSection.
 */

import { useCallback, useMemo, useState } from 'react';
import { BookOpen, FileCode2, ChevronDown } from 'lucide-react';
import { Button } from '../../../../components/ui/button';
import { cn } from '../../../../lib/utils';
import { toast } from '../../../../components/ui/toaster';
import { AnimatedCollapse } from './AnimatedCollapse';
import { useScriptBrowserStore } from '../../../script-browser/store/scriptBrowserStore';
import { useSidebarStore } from '../../../database-objects/store/sidebarStore';
import { openScriptInTab } from '../../../script-browser/services/scriptBrowserApi';
import { FileNotFoundDialog } from '../../../script-browser/components/FileNotFoundDialog';
import { formatRelativeTime } from '../utils/timestampFormatting';
import type { ScriptEntry } from '../../../script-browser/types/scriptBrowserTypes';
import { safeServerText } from '../../../../types/safeConnectionDisplay';

const MAX_RECENT_SCRIPTS = 5;

type RecentScriptsSectionProps = {
  isExpanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
};

export function RecentScriptsSection({
  isExpanded,
  onExpandedChange,
}: RecentScriptsSectionProps) {
  const scripts = useScriptBrowserStore((s) => s.scripts);

  const recentScripts = useMemo(
    () =>
      [...scripts]
        .sort(
          (a, b) =>
            new Date(b.lastOpenedAt).getTime() -
            new Date(a.lastOpenedAt).getTime()
        )
        .slice(0, MAX_RECENT_SCRIPTS),
    [scripts]
  );

  const handleToggle = useCallback(() => {
    onExpandedChange(!isExpanded);
  }, [isExpanded, onExpandedChange]);

  const removeScript = useScriptBrowserStore((s) => s.removeScript);
  const [fileNotFoundScript, setFileNotFoundScript] =
    useState<ScriptEntry | null>(null);

  const handleOpen = useCallback(async (script: ScriptEntry) => {
    const result = await openScriptInTab(script.filePath);
    if (result === 'file-not-found') {
      setFileNotFoundScript(script);
    } else if (result === null) {
      toast.error({
        title: 'Failed to open script',
        description: script.filePath,
      });
    }
  }, []);

  const handleViewAll = useCallback(() => {
    const store = useSidebarStore.getState();
    if (store.isCollapsed) store.expand();
    if (store.scriptsCollapsed) store.expandScriptsPanel();
  }, []);

  // Completely hidden when no scripts
  if (scripts.length === 0) {
    return null;
  }

  return (
    <div
      className="rounded-lg border bg-surface-raised shadow-sm"
      data-testid="recent-scripts-section"
    >
      {/* Section Header */}
      <Button
        variant="ghost"
        className={cn(
          'w-full flex items-center justify-between px-4 py-3 h-auto',
          isExpanded && 'rounded-b-none'
        )}
        onClick={handleToggle}
        data-testid="recent-scripts-section-toggle"
      >
        <div className="flex items-center gap-2">
          <FileCode2 className="h-4 w-4" />
          <span className="text-sm font-medium">Recent Scripts</span>
          <span className="text-xs text-muted-foreground tabular-nums">
            ({recentScripts.length})
          </span>
        </div>
        <ChevronDown
          className={cn('h-4 w-4 transition-transform duration-200', !isExpanded && '-rotate-90')}
        />
      </Button>

      {/* Section Content */}
      <AnimatedCollapse isOpen={isExpanded}>
        <div className="px-4 pb-3 pt-1" data-testid="recent-scripts-section-content">
          <div className="overflow-hidden rounded-md border border-border/50">
            {recentScripts.map((script) => (
              <RecentScriptRow
                key={script.id}
                script={script}
                onOpen={handleOpen}
              />
            ))}
          </div>

          {/* Footer link */}
          <button
            type="button"
            className="mt-0 -mx-2 -mb-2 inline-flex items-center rounded-md px-2 py-2 text-xs text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
            onClick={handleViewAll}
            data-testid="recent-scripts-view-all"
          >
            View all in Scripts panel
          </button>
        </div>
      </AnimatedCollapse>

      {fileNotFoundScript && (
        <FileNotFoundDialog
          script={fileNotFoundScript}
          onRemove={() => {
            removeScript(fileNotFoundScript.id);
            setFileNotFoundScript(null);
          }}
          onKeep={() => setFileNotFoundScript(null)}
        />
      )}
    </div>
  );
}

// ─── Row sub-component ────────────────────────────────────────────────

type RecentScriptRowProps = {
  script: ScriptEntry;
  onOpen: (script: ScriptEntry) => void;
};

function RecentScriptRow({ script, onOpen }: RecentScriptRowProps) {
  const relativeTime = formatRelativeTime(new Date(script.lastOpenedAt));
  const Icon = script.fileType === 'ipynb' ? BookOpen : FileCode2;

  return (
    <button
      type="button"
      className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-accent/50 transition-colors cursor-pointer border-b border-border/30 last:border-b-0"
      onClick={() => onOpen(script)}
      title={script.filePath}
      data-testid="recent-script-row"
    >
      <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
      <span className="truncate font-medium flex-1">{script.fileName}</span>
      {script.connectionName && (
        <span className="text-xs text-muted-foreground truncate max-w-[120px]">
          {safeServerText(script.connectionName)}
        </span>
      )}
      <span className="text-xs text-muted-foreground whitespace-nowrap">
        {relativeTime}
      </span>
    </button>
  );
}
